import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DefaultIcon, FileContentType, IFileReference, isString } from '@summit/core';
import { DisplaySchema, SummitDisposing, SummitIdObject } from '@summit/models';
import { SummitNotificationService } from '@summit/services';
import { ApiError } from '@summit/ui';
import { Button } from 'primeng/button';
import { ProgressBar } from 'primeng/progressbar';
import { Step, StepList, Stepper } from 'primeng/stepper';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  takeUntil
} from 'rxjs';
import { SFormWizardStepDirective } from '../../directives/s-form-wizard-step.directive';
import { SummitWizardStep } from '../../models';
import { SummitWizardStepLayout } from '../../models/summit-wizard-step-layout.enum';
import { SFormFileViewerComponent } from '../s-form-file-viewer/s-form-file-viewer.component';
import { SFormInputFileComponent } from '../s-form-input-file/s-form-input-file.component';
import { SFormInputComponent } from '../s-form-input/s-form-input.component';
import { SFormPageComponent } from '../s-form-page/s-form-page.component';
@Component({
  selector: 's-form-wizard',
  templateUrl: './s-form-wizard.component.html',
  styleUrls: ['./s-form-wizard.component.css'],
  standalone: true,
  imports: [
    ProgressBar,
    Button,
    NgTemplateOutlet,
    AsyncPipe,
    SFormPageComponent,
    SFormInputComponent,
    SFormFileViewerComponent,
    StepList,
    Step,
    Stepper
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormWizardComponent<T extends SummitIdObject> extends SummitDisposing implements OnInit, AfterViewInit {
  //FormPage Inputs
  @Input() form: FormGroup;
  @Input() apiError: ApiError;
  @Input() schema: DisplaySchema<T>;
  @Input() submitting$: Observable<boolean>;
  @Input() allowIndexChange: (id: string) => Promise<boolean>;
  @Input() showSingleStep = false;
  @Input() showSave: boolean;
  @Input() saveLabel: string;
  @Input() saveLabel$: Observable<string> | undefined;
  @Input() backLabel: string;
  @Input() backLabel$: Observable<string> | undefined;
  @Input() nextLabel: string;
  @Input() nextLabel$: Observable<string> | undefined;
  @Input() showLastNext: boolean;
  @Input() loaded$: Observable<boolean> = of(true);

  //Wizard Inputs
  @Input() steps$: Observable<SummitWizardStep<T>[]>;
  @Input() applicableFields$: Observable<(keyof T)[]>;
  @Input() getContext: () => unknown;
  @Input() fileUpload: SFormInputFileComponent<T>;
  @Input() fileRefs$: Observable<IFileReference[]>;
  @Output() activeIndexChange = new EventEmitter<string>();
  @Output() save = new EventEmitter();
  @Output() nextOnLast = new EventEmitter();
  @ContentChildren(SFormWizardStepDirective) parentStepPages: QueryList<SFormWizardStepDirective>;
  @ContentChild('footer') footer: TemplateRef<unknown>;
  //private lclSteps: SummitWizardStep<T>[];
  currentSteps: SummitWizardStep<T>[];
  allCurrentFields: (keyof T & string)[] = [];
  preRenderStepsBs = new BehaviorSubject<SFormWizardStepDirective[]>([]);
  preRenderSteps$ = this.preRenderStepsBs.asObservable();
  activeStepIdBs: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  activeStepId$: Observable<string | undefined> = this.activeStepIdBs.asObservable().pipe(distinctUntilChanged());
  getActiveStepId = () => this.activeStepIdBs.value;
  activeStep$: Observable<SummitWizardStep<T>>;
  activeStepIndex$: Observable<number>;
  activeStepValue$: Observable<number>;

  isCurrentPreRendered$: Observable<boolean>;
  private loadReady = new BehaviorSubject<boolean>(false);
  loadReady$ = this.loadReady.asObservable();
  loadAbleBs = new BehaviorSubject<boolean>(false);
  loadAble$ = this.loadAbleBs.asObservable();
  private fullyLoaded: boolean;

  displayTemplate$: Observable<TemplateRef<unknown> | undefined>;
  stepLayouts = SummitWizardStepLayout;
  defaultIcon = DefaultIcon;

  progressBarValue$: Observable<number>;

  testMessages$: Observable<any[]>;

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private notificationService: SummitNotificationService
  ) {
    super();
    this.showSave ??= true;
    //NOP
  }

  ngOnInit() {
    //Get starting step from query params
    const resumeTag = this.activeRoute.snapshot.queryParams['step'];
    if (resumeTag) this.activeStepIdBs.next(resumeTag);
    combineLatest([this.loaded$, this.steps$])
      .pipe(takeUntil(this.disposing$))
      .subscribe(([loaded, steps]) => {
        //local copy
        if (!loaded) return;
        //console.log(`settings steps:`, loaded, steps);
        if (!this.activeStepIdBs.value) {
          const firstStep = steps[0];
          this.activeStepIdBs.next(firstStep.id);
        }
        this.currentSteps = steps;
        this.allCurrentFields = this.getAllCurrentFields();
        this.fullyLoaded = true; //need to keep correct start index
      });
    this.activeStep$ = combineLatest([this.steps$, this.activeStepId$]).pipe(
      map(([steps, id]) => steps.find((step) => step.id === id) ?? steps[0]),
      distinctUntilKeyChanged('id'),
      shareReplay(1)
    );
    this.activeStepIndex$ = combineLatest([this.steps$, this.activeStepId$]).pipe(
      // tap(([steps, id]) => {
      //   console.log(`activeStepIndex:`, steps, id);
      // }),
      map(([steps, id]) => steps.findIndex((step) => step.id === id)),
      map((id) => (id < 0 ? 0 : id)),
      // tap((index) => {
      //   console.log(`activeStepIndex:`, index);
      // }),
      distinctUntilChanged(),
      shareReplay(1)
    );
    this.activeStepValue$ = this.activeStepIndex$.pipe(
      map((step) => step + 1),
      shareReplay(1)
    );

    this.activeStep$.pipe(takeUntil(this.disposing$)).subscribe((item) => {
      //console.log(`activeStep sub:`, item);
      this.onActiveIndexChange(item?.id);
      if (item?.onLoad) item.onLoad(this.form, this.form.value, this.allCurrentFields, this.getContext?.());
    });
    this.activeStepId$ = this.activeStep$.pipe(map((step) => step?.id));
    this.isCurrentPreRendered$ = combineLatest([this.activeStep$, this.preRenderSteps$]).pipe(
      map(([currentStep, preRenderSteps]) => {
        //console.log(`isCurrentPreRendered:`, currentStep, preRenderSteps);
        return preRenderSteps.findIndex((step) => step.wizardStep === currentStep?.id) >= 0;
      })
    );
    combineLatest([this.loaded$, this.loadReady$])
      .pipe(takeUntil(this.disposing$))
      .subscribe(([loaded, loadReady]) => {
        if (loaded && loadReady) {
          this.loadAbleBs.next(true);
        }
      });

    this.progressBarValue$ = combineLatest([this.activeStepIndex$, this.steps$]).pipe(
      map(([index, steps]) => {
        if (!steps || steps.length === 0) return 0;
        const percent = (index / steps.length) * 100;
        return Math.round(percent);
      })
    );
    this.displayTemplate$ = this.activeStep$.pipe(
      map((step) => this.parentStepPages?.find((item) => item.wizardStep === step?.id)?.template)
    );
  }

  ngAfterViewInit() {
    const preRenderSteps: SFormWizardStepDirective[] = [];
    this.parentStepPages.forEach((step) => {
      //console.log(`step:`, step, step.preRender);
      if (step.preRender) {
        preRenderSteps.push(step);
      }
    });
    //console.log(`preRenderSteps:`, preRenderSteps);
    this.preRenderStepsBs.next(preRenderSteps);
    this.loadStep();
    this.linkTestMessages();
    this.subscribeFileRefs();
  }

  linkTestMessages() {
    this.testMessages$ = this.fileUpload?.newFileRefs$;
    if (!this.testMessages$) {
      setTimeout(() => {
        this.linkTestMessages();
      }, 100);
    } else {
      console.error(`linked testMessages$`, this.testMessages$);
    }
  }

  subscribeFileRefs() {
    if (this.fileRefs$) {
      this.fileRefs$.pipe(takeUntil(this.disposing$)).subscribe((fileRefs) => {
        //console.log(`fileRefs:`, fileRefs);
        this.cd.detectChanges();
      });
    } else {
      setTimeout(() => {
        this.subscribeFileRefs();
      }, 100);
    }
  }

  async goToNextStep() {
    this.nextStep();
  }

  goToStep(id: string): void {
    this.activeStepIdBs.next(id);
    return;
  }

  async getStepIndex(id?: string): Promise<number> {
    id ??= this.activeStepIdBs.value;
    const steps = await firstValueFrom(this.steps$);
    return steps.findIndex((item) => item.id === id);
  }
  public async loadStep() {
    this.cd.detectChanges();
  }

  //number needed to support the stepper
  async onActiveIndexChange(id: string | number | undefined) {
    id ??= 0;
    //console.log(`form wiz: onActiveIndexChange: going to`, id);
    if (!this.fullyLoaded) return;
    const steps = await firstValueFrom(this.steps$);
    var stepId = isString(id) ? id : steps[id - 1]?.id;
    if (!id) return;
    // console.log(`onActiveIndexChange:`, stepId);
    this.loadStep();
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { step: id },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      skipLocationChange: false
    });
    if (stepId !== this.activeStepIdBs.value) {
      this.activeStepIdBs.next(stepId);
    }
    this.activeIndexChange.emit(stepId); //need outside of if
    this.cd.detectChanges();
  }

  async nextStep() {
    const steps = await firstValueFrom(this.steps$);
    let lclActiveStepIndex = steps.findIndex((item) => item.id === this.activeStepIdBs.value);
    const leavingStep = steps[lclActiveStepIndex];
    if (!leavingStep) {
      this.goToStep(steps[0].id);
      return;
    }
    if (
      leavingStep.allowNext &&
      !leavingStep.allowNext(this.form, this.form.value, this.allCurrentFields, this.getContext?.())
    ) {
      console.log(`next Step: blocked by allowNext`);
      return;
    }
    //console.log(`nextStep:`, lclActiveStepIndex, steps.length);
    lclActiveStepIndex++;
    if (lclActiveStepIndex >= steps.length) {
      this.nextOnLast.emit();
      lclActiveStepIndex = steps.length - 1; //safe guard
    }
    const newId = steps[lclActiveStepIndex]?.id;
    //Test if form is valid up to this point
    this.form.updateValueAndValidity();
    const reviewedWizardFields = this.getReviewFields(leavingStep.id);
    //console.log(`reviewedWizardFields:`, reviewedWizardFields);
    reviewedWizardFields.forEach((field) => {
      this.form.get(field?.toString())?.markAsTouched();
    });
    this.cd.detectChanges();
    const currentStepId = this.getActiveStepId();
    const currentIndex = this.currentSteps?.findIndex((step) => step.id === currentStepId);
    const newIndex = this.currentSteps?.findIndex((step) => step.id === newId);
    if (newIndex > currentIndex && !this.form.valid) {
      for (let index = 0; index < reviewedWizardFields.length; index++) {
        const field = reviewedWizardFields[index];
        const control = this.form.get(field?.toString());
        if (control?.invalid) {
          // console.warn(
          //   `Next Step Blocked: form error`,
          //   field,
          //   this.form.get(field?.toString())?.errors,
          //   this.form.get(field?.toString())?.value
          // );
          return;
        }
      }
    }

    if (this.allowIndexChange && !(await this.allowIndexChange(newId))) {
      console.log(`nextStep: not allowed`);
      return;
    }
    //console.log(`nextStep newId:`, newId);
    if (leavingStep.onNext) leavingStep.onNext(this.form, this.form.value, this.allCurrentFields, this.getContext?.());
    this.activeStepIdBs.next(newId);
  }
  async priorStep() {
    const steps = await firstValueFrom(this.steps$);
    let lclActiveStepIndex = steps.findIndex((item) => item.id === this.activeStepIdBs.value);
    lclActiveStepIndex--;
    if (lclActiveStepIndex < 0) lclActiveStepIndex = 0; //safe guard
    const newId = steps[lclActiveStepIndex]?.id;
    if (this.allowIndexChange && !(await this.allowIndexChange(newId))) {
      console.log(`priorStep: not allowed`);
      return;
    }
    //console.log(`going to newId:`, newId);
    this.activeStepIdBs.next(newId);
  }

  async triggerSave() {
    this.save.emit();
  }

  getStep(id: string | undefined): SummitWizardStep<T> | undefined {
    return this.currentSteps?.find((item) => item.id === id) ?? this.currentSteps?.[0];
  }

  setAndGo(field: string, value: string | number | undefined) {
    this.form.get(field)?.setValue(value);
    const buttonInfo = this.getStep(this.getActiveStepId())
      ?.inputLayout?.reduce((accumulator, value) => accumulator.concat(value), [])
      .find((item) => item.field === field && value === item.value);
    if (buttonInfo?.onChange) {
      buttonInfo.onChange(this.form, field, value);
    }
    this.goToNextStep();
  }

  async attachContentType($event: Event, contentType: FileContentType, retryCount: number = 5) {
    $event.preventDefault();
    if (!this.fileUpload) {
      // if (retryCount <= 0) {
      this.notificationService.triggerToast('Uploader failed to load. Please Refresh and retry', 3, 'warn');
      //   return;
      // }
      // setTimeout(() => {
      //   this.attachContentType($event, contentType, retryCount - 1);
      // }, 200);
      return;
    }
    //console.warn(`attachContentType:`, this.fileUpload);
    this.fileUpload.choose().then((newFiles) => {
      //console.warn(`choose:`, newFiles);
      //handleFiles(newFiles);
      newFiles.forEach((fileRef) => {
        this.fileUpload.updateUploadContentType(fileRef, contentType);
      });
      this.cd.detectChanges();
    });
  }

  getReviewFields(stepId?: string): (keyof T & string)[] {
    stepId ??= this.getActiveStepId();
    const steps = this.currentSteps ?? [];
    const currentIndex = steps.findIndex((lclStep) => lclStep.id === stepId);
    if (currentIndex === undefined || currentIndex === null || currentIndex < 0) return [];
    return [...steps]
      .slice(0, currentIndex + 1)
      .map((step) => step.fields)
      .flat()
      .filter((field) => !!field) as (keyof T & string)[];
  }

  refresh() {
    window.location.reload();
  }

  getAllCurrentFields(): (keyof T & string)[] {
    return this.currentSteps
      .map((step) => step.fields)
      .flat()
      .filter((field) => !!field) as (keyof T & string)[];
  }
}
